/**
 * <header-split-menu>
 *
 * Used by the "Logo center, menu left" header layout to keep the logo
 * perfectly centered while overflowing menu items spill to the right side
 * of the logo (instead of pushing the logo around).
 *
 * Markup:
 *   <header-split-menu
 *     data-overflow-target=".header__menu--overflow"
 *     data-logo-target=".header__desktop__bar__c">
 *     <nav data-menu-primary class="header__menu header__menu--primary">
 *       ...nav items rendered here...
 *     </nav>
 *   </header-split-menu>
 *
 *   ...
 *   <nav class="header__menu header__menu--overflow"></nav>
 */
(function () {
  if (typeof window === 'undefined') return;
  if (window.customElements && window.customElements.get('header-split-menu')) return;

  const READY_CLASS = 'is-ready';
  const ITEM_SELECTOR = '.menu__item';
  const HOVER_TAIL_SELECTOR = '.hover__bar, .hover__bg';
  const COLLISION_BUFFER_PX = 8;
  const DESKTOP_MIN_WIDTH = 750;

  class HeaderSplitMenu extends HTMLElement {
    constructor() {
      super();
      this._scheduleUpdate = this._scheduleUpdate.bind(this);
      this._update = this._update.bind(this);
    }

    connectedCallback() {
      this.primary = this.querySelector('[data-menu-primary]');
      const overflowSelector = this.dataset.overflowTarget;
      const logoSelector = this.dataset.logoTarget;
      this.section = this.closest('[data-section-id]') || document;
      this.overflow = overflowSelector ? this.section.querySelector(overflowSelector) : null;
      this.logo = logoSelector ? this.section.querySelector(logoSelector) : null;
      this.wrapper = this.closest('[data-takes-space-wrapper]') || this.parentElement;

      if (!this.primary || !this.overflow || !this.logo || !this.wrapper) {
        this.classList.add(READY_CLASS);
        return;
      }

      this._frameId = 0;
      this._isUpdating = false;

      if ('ResizeObserver' in window) {
        this._resizeObserver = new ResizeObserver(this._scheduleUpdate);
        this._resizeObserver.observe(this.wrapper);
        this._resizeObserver.observe(this.logo);
      } else {
        window.addEventListener('resize', this._scheduleUpdate);
      }

      this._scheduleUpdate();

      if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
        document.fonts.ready.then(this._scheduleUpdate);
      }
    }

    disconnectedCallback() {
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', this._scheduleUpdate);
      }
      if (this._frameId) cancelAnimationFrame(this._frameId);
    }

    _scheduleUpdate() {
      if (this._frameId) cancelAnimationFrame(this._frameId);
      this._frameId = requestAnimationFrame(this._update);
    }

    _isVisible() {
      if (window.innerWidth < DESKTOP_MIN_WIDTH) return false;
      return this.offsetWidth > 0 && this.offsetHeight > 0;
    }

    _update() {
      this._frameId = 0;
      if (this._isUpdating) return;
      if (!this._isVisible()) return;

      this._isUpdating = true;

      try {
        this._collapseAll();

        const logoRect = this.logo.getBoundingClientRect();
        if (logoRect.width === 0 || logoRect.height === 0) {
          this.classList.add(READY_CLASS);
          return;
        }

        const collisionBoundary = logoRect.left - COLLISION_BUFFER_PX;
        const items = this._primaryItems();

        let firstOverflowIndex = -1;
        for (let i = 0; i < items.length; i++) {
          const rect = items[i].getBoundingClientRect();
          if (rect.right > collisionBoundary) {
            firstOverflowIndex = i;
            break;
          }
        }

        if (firstOverflowIndex !== -1) {
          for (let i = firstOverflowIndex; i < items.length; i++) {
            this.overflow.appendChild(items[i]);
          }
        }

        this.classList.add(READY_CLASS);
      } finally {
        this._isUpdating = false;
      }
    }

    _primaryItems() {
      return Array.prototype.filter.call(
        this.primary.children,
        (el) => el.matches && el.matches(ITEM_SELECTOR)
      );
    }

    _collapseAll() {
      const tail = this.primary.querySelector(HOVER_TAIL_SELECTOR);
      while (this.overflow.firstElementChild) {
        const node = this.overflow.firstElementChild;
        if (tail) {
          this.primary.insertBefore(node, tail);
        } else {
          this.primary.appendChild(node);
        }
      }
    }
  }

  window.customElements.define('header-split-menu', HeaderSplitMenu);
})();
